import {
  FileCheck2, FileSignature, FileText, FolderOpen, ReceiptText, User,
} from 'lucide-react'
import DocumentCard from './DocumentCard'

const TYPE_ICONS = {
  QUOTE: ReceiptText,
  CONTRACT: FileSignature,
  INVOICE: FileCheck2,
  CV: User,
}

const formatListDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const CvListItem = ({ label, date, onView }) => (
  <button
    type="button"
    onClick={onView}
    className="w-full flex items-center gap-3 p-3 rounded-xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary)/0.08)] hover:bg-[hsl(var(--primary)/0.15)] transition-colors text-left"
  >
    <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0">
      <FileText className="w-5 h-5 text-[hsl(var(--primary))]" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-slate-900 truncate">{label}</p>
      <p className="text-xs text-slate-500">CV · {date ? formatListDate(date) : 'Candidature'}</p>
    </div>
    <span className="text-xs font-medium text-[hsl(var(--primary))] shrink-0">Voir</span>
  </button>
)

export default function ChatDocumentsList({
  cvEntry,
  documents,
  currentUserId,
  onViewCv,
  onViewDocument,
  loading,
}) {
  const sortedDocuments = [...documents].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
  const totalCount = sortedDocuments.length + (cvEntry ? 1 : 0)

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse h-16 bg-slate-100 rounded-xl" />
        ))}
      </div>
    )
  }

  if (totalCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <FolderOpen className="w-12 h-12 text-slate-300 mb-3" />
        <p className="font-medium text-slate-900">Aucun fichier</p>
        <p className="text-sm text-slate-500 mt-1">
          Les CV et documents partagés dans cette conversation apparaîtront ici.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 py-3 border-b border-slate-200 shrink-0">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-[hsl(var(--primary))]" />
          Fichiers ({totalCount})
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
        {cvEntry && (
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
              CV
            </h4>
            <CvListItem
              label={cvEntry.label}
              date={cvEntry.date}
              onView={onViewCv}
            />
          </section>
        )}

        {sortedDocuments.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
              Documents ({sortedDocuments.length})
            </h4>
            <div className="space-y-3">
              {sortedDocuments.map((doc) => {
                const isIssuer = doc.issuerUserId === currentUserId
                const TypeIcon = TYPE_ICONS[doc.type] ?? FileText
                return (
                  <div key={doc.id} className="space-y-2">
                    <DocumentCard
                      document={doc}
                      onPress={() => onViewDocument(doc)}
                    />
                    <div className="flex items-center justify-between px-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <TypeIcon className="w-3 h-3" />
                        {isIssuer ? 'Envoyé' : 'Reçu'} · {formatListDate(doc.createdAt)}
                      </span>
                      <button
                        type="button"
                        onClick={() => onViewDocument(doc)}
                        className="font-medium text-[hsl(var(--primary))] hover:underline"
                      >
                        Prévisualiser
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
